/**
 * SPEC-annotation-review-mode (Phase 2) — admin queue page.
 *
 * Lists deliveries that contain at least one region of one of the four
 * supported annotation classes (default: cookunity_bag), filtered by
 * review_status (default: pending), dz_type, and date range. Clicking a row
 * navigates to qa-detail-v3 in review mode (`?review=1`); the actual
 * review-mode UI (banner, verdict swap, keyboard shortcuts) is Phase 3.
 *
 * Filters live in the URL/query string so refreshes and "back" preserve
 * state — and so deep links into a specific filtered view work.
 *
 * The component is intentionally thin: every list-shaping decision (filter
 * defaults, ordering, per-class counts, dzType chips, the per-row link
 * target) happens in the GET /api/admin/annotation-review/queue handler
 * + storage method, where it's covered by the integration test suite. This
 * page just renders what the API returns.
 */
import { useMemo, useState, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useSearch, Link } from "wouter";
import {
  RESUME_LOCALSTORAGE_KEY,
  formatClaimTimeLeft,
} from "@/lib/annotation-review";
import { useAuth } from "@/hooks/use-auth";
import { useAnnotationReviewSocket } from "@/hooks/use-annotation-review-socket";
import {
  REVIEW_CLAIM_TTL_MS,
  type ReviewUpdateEvent,
} from "@shared/annotation-review-claims";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, ChevronRight, ClipboardCheck, AlertCircle } from "lucide-react";

const ALL_CLASSES = ["cookunity_bag", "building_number", "unit_number", "label"] as const;

type ReviewStatusFilter = "pending" | "verified" | "discarded" | "any";

interface QueueRow {
  id: string;
  thumbnailUrl: string | null;
  dzTypes: string[];
  regionReviewStatus: "pending" | "verified" | "discarded";
  regionCountsByClass: Record<string, number>;
  detailHref: string;
  // SPEC-annotation-review-concurrency Phase 6 — populated only when the
  // include_claimed=1 toggle is on AND another reviewer holds an active
  // claim on this row. Null/absent on rows the caller can act on freely.
  claimerId?: string | null;
  claimerName?: string | null;
  claimedAt?: string | null;
  // SPEC-annotation-review-seed-sources Phase 2 — per-row seed source.
  // Null on rows that pre-date the spec or were never recruited into a
  // named batch.
  seedSource?: string | null;
}

interface QueueResponse {
  total: number;
  rows: QueueRow[];
}

const PAGE_SIZE = 50;

function parseFilters(search: string): {
  classes: string[];
  reviewStatus: ReviewStatusFilter;
  dzType: string;
  dateFrom: string;
  dateTo: string;
  offset: number;
  /** SPEC-annotation-review-concurrency Phase 6 — "Show in-progress" toggle. */
  showInProgress: boolean;
  /** SPEC-annotation-review-seed-sources Phase 4 — multi-select on region_review_seed_source. */
  seedSources: string[];
} {
  const p = new URLSearchParams(search);
  const classRaw = p.get("class");
  const classes = classRaw
    ? classRaw.split(",").map((s) => s.trim()).filter(Boolean)
    : [];
  const reviewStatusRaw = (p.get("review_status") ?? "pending") as ReviewStatusFilter;
  const allowed: ReviewStatusFilter[] = ["pending", "verified", "discarded", "any"];
  const reviewStatus = allowed.includes(reviewStatusRaw) ? reviewStatusRaw : "pending";
  const dzType = p.get("dz_type") ?? "";
  const dateFrom = p.get("date_from") ?? "";
  const dateTo = p.get("date_to") ?? "";
  const offsetRaw = parseInt(p.get("offset") ?? "0", 10);
  const offset = Number.isFinite(offsetRaw) && offsetRaw >= 0 ? offsetRaw : 0;
  const showInProgressRaw = p.get("include_claimed");
  const showInProgress = showInProgressRaw === "1" || showInProgressRaw === "true";
  const seedSourceRaw = p.get("seed_source");
  const seedSources = seedSourceRaw
    ? seedSourceRaw.split(",").map((s) => s.trim()).filter(Boolean)
    : [];
  return { classes, reviewStatus, dzType, dateFrom, dateTo, offset, showInProgress, seedSources };
}

function buildQueryString(f: {
  classes: string[];
  reviewStatus: ReviewStatusFilter;
  dzType: string;
  dateFrom: string;
  dateTo: string;
  offset: number;
  limit: number;
  showInProgress?: boolean;
  /** SPEC-annotation-review-seed-sources Phase 4 — multi-select. */
  seedSources?: string[];
}): string {
  const p = new URLSearchParams();
  if (f.classes.length > 0) p.set("class", f.classes.join(","));
  p.set("review_status", f.reviewStatus);
  if (f.dzType) p.set("dz_type", f.dzType);
  if (f.dateFrom) p.set("date_from", f.dateFrom);
  if (f.dateTo) p.set("date_to", f.dateTo);
  p.set("limit", String(f.limit));
  p.set("offset", String(f.offset));
  if (f.showInProgress) p.set("include_claimed", "1");
  if (f.seedSources && f.seedSources.length > 0) {
    p.set("seed_source", f.seedSources.join(","));
  }
  return p.toString();
}

function StatusBadge({ status }: { status: "pending" | "verified" | "discarded" }) {
  switch (status) {
    case "verified":
      return (
        <Badge className="bg-green-600 dark:bg-green-700" data-testid="badge-status-verified">
          Verified
        </Badge>
      );
    case "discarded":
      return (
        <Badge variant="destructive" data-testid="badge-status-discarded">
          Discarded
        </Badge>
      );
    default:
      return (
        <Badge variant="secondary" data-testid="badge-status-pending">
          Pending
        </Badge>
      );
  }
}

export default function AnnotationReview() {
  const search = useSearch();
  const [, setLocation] = useLocation();
  const filters = useMemo(() => parseFilters(search), [search]);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // SPEC-annotation-review-mode (Phase 3) — resume position. The qa-detail-v3
  // review-mode page writes the most recently touched delivery id to
  // localStorage on every navigation. When Sarah re-opens the queue page we
  // surface a banner offering to jump back into review mode for that
  // delivery, with the same filter set already applied. Dismissing the
  // banner clears the entry — opt-in resume only.
  const [resumeId, setResumeId] = useState<string | null>(null);
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RESUME_LOCALSTORAGE_KEY);
      if (stored) setResumeId(stored);
    } catch {
      /* localStorage may be unavailable; just don't offer resume */
    }
  }, []);
  const dismissResume = () => {
    setResumeId(null);
    try { localStorage.removeItem(RESUME_LOCALSTORAGE_KEY); } catch { /* ignore */ }
  };
  const followResume = () => {
    if (!resumeId) return;
    const params = new URLSearchParams(search);
    params.set("review", "1");
    setLocation(`/qa/delivery/${resumeId}?${params.toString()}`);
  };

  // Build the API query string. Mirrors `buildQueryString` for the URL but
  // always includes limit (the URL-facing "offset" is the user-visible
  // pagination cursor; limit is fixed for now at PAGE_SIZE).
  const apiQuery = useMemo(
    () =>
      buildQueryString({
        classes: filters.classes,
        reviewStatus: filters.reviewStatus,
        dzType: filters.dzType,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        offset: filters.offset,
        limit: PAGE_SIZE,
        showInProgress: filters.showInProgress,
        seedSources: filters.seedSources,
      }),
    [filters],
  );

  const { data, isLoading, error } = useQuery<QueueResponse>({
    queryKey: ["/api/admin/annotation-review/queue", apiQuery],
    queryFn: async () => {
      const res = await fetch(`/api/admin/annotation-review/queue?${apiQuery}`, {
        credentials: "include",
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`${res.status}: ${txt || res.statusText}`);
      }
      return res.json();
    },
  });

  // SPEC-annotation-review-concurrency Phase 6 — subscribe to review:update.
  // When ANY review:update event arrives we invalidate the queue cache so
  // the visible row set reflects the new state in real time. Same for the
  // neighbors cache the detail page consumes — refetching is cheap and the
  // alternative (mutate cache locally per event) is several edge cases of
  // bookkeeping for no real win.
  const handleReviewUpdate = useCallback(
    (_event: ReviewUpdateEvent) => {
      // Coarse invalidation matches the spec's note: "Clients filter
      // review:update events client-side and decide whether to invalidate
      // the React Query cache" — for the queue page, every event is
      // potentially relevant since the visibility filter on the server
      // depends on claim state.
      queryClient.invalidateQueries({ queryKey: ["/api/admin/annotation-review/queue"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/annotation-review/neighbors"] });
    },
    [queryClient],
  );

  useAnnotationReviewSocket(
    user?.id ?? null,
    handleReviewUpdate,
    { userName: user?.name || undefined, email: user?.email || undefined },
  );

  const updateUrl = (next: Partial<typeof filters>) => {
    const merged = { ...filters, ...next };
    // Reset offset to 0 whenever a filter (other than offset itself) changes.
    if (!("offset" in next)) merged.offset = 0;
    const qs = buildQueryString({
      ...merged,
      limit: PAGE_SIZE,
      showInProgress: merged.showInProgress,
      seedSources: merged.seedSources,
    });
    setLocation(`/admin/annotation-review?${qs}`, { replace: false });
  };

  const total = data?.total ?? 0;
  const rows = data?.rows ?? [];
  const pageStart = total === 0 ? 0 : filters.offset + 1;
  const pageEnd = Math.min(filters.offset + rows.length, total);

  const toggleClass = (cls: string) => {
    const isSelected = filters.classes.includes(cls);
    const next = isSelected
      ? filters.classes.filter((c) => c !== cls)
      : [...filters.classes, cls];
    updateUrl({ classes: next });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="annotation-review-page">
      <div className="flex items-center gap-3">
        <ClipboardCheck className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-xl font-semibold" data-testid="text-annotation-review-title">
            Annotation Review
          </h1>
          <p className="text-sm text-muted-foreground">
            Walk the cookunity_bag annotation seed set and add building_number, unit_number, and
            label regions for YOLO multi-class retraining.
          </p>
        </div>
      </div>

      {/*
        SPEC-annotation-review-seed-sources Phase 4 — batch chip. Shown only
        when the URL carries a ?seed_source filter so Sarah can see which
        recruitment batch she's walking. Count comes from the queue API's
        `total` field, which already reflects the seed_source filter at the
        SQL layer (see Q6 / Q6-DB tests). No control to clear the batch is
        rendered here on purpose: batches are reached via direct URL or
        bookmark per the spec, and the cleanest "leave the batch" UX is
        navigating to /admin/annotation-review with no params.
      */}
      {filters.seedSources.length > 0 && (
        <div
          className="flex items-center gap-2 rounded-md border px-3 py-2"
          style={{ background: "rgba(59, 130, 246, 0.08)", borderColor: "rgba(37, 99, 235, 0.25)" }}
          data-testid="batch-chip"
        >
          <Badge variant="outline" data-testid="batch-chip-name">
            Batch: {filters.seedSources.join(", ")}
          </Badge>
          <span className="text-sm text-muted-foreground" data-testid="batch-chip-count">
            {isLoading
              ? "loading…"
              : `${(data?.total ?? 0).toLocaleString()} ${(data?.total ?? 0) === 1 ? "delivery" : "deliveries"}`}
          </span>
        </div>
      )}

      {resumeId && (
        <div
          className="flex items-center justify-between gap-3 rounded-md border px-4 py-3"
          style={{ background: "rgba(251, 191, 36, 0.12)", borderColor: "rgba(217, 119, 6, 0.3)" }}
          data-testid="banner-resume"
        >
          <div className="flex items-center gap-2 text-sm">
            <ClipboardCheck className="h-4 w-4 text-amber-700" />
            <span>
              Resume review at delivery <span className="font-mono">{resumeId}</span>?
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="default" onClick={followResume} data-testid="button-resume-go">
              Resume
            </Button>
            <Button size="sm" variant="ghost" onClick={dismissResume} data-testid="button-resume-dismiss">
              Dismiss
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
          <CardDescription>
            Filters live in the URL — copy/paste preserves your view.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="text-sm font-medium mb-2">Class</div>
            <div className="flex flex-wrap gap-2" data-testid="filter-classes">
              {ALL_CLASSES.map((cls) => {
                // An empty `classes` selection means "default = cookunity_bag"
                // (matching the API). Reflect that visually.
                const effective =
                  filters.classes.length > 0 ? filters.classes : ["cookunity_bag"];
                const active = effective.includes(cls);
                return (
                  <Button
                    key={cls}
                    variant={active ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleClass(cls)}
                    data-testid={`filter-class-${cls}`}
                  >
                    {cls}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <div className="text-sm font-medium mb-1">Review status</div>
              <Select
                value={filters.reviewStatus}
                onValueChange={(v) => updateUrl({ reviewStatus: v as ReviewStatusFilter })}
              >
                <SelectTrigger data-testid="filter-review-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="discarded">Discarded</SelectItem>
                  <SelectItem value="any">Any</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-sm font-medium mb-1">dz_type (optional)</div>
              <Input
                placeholder="e.g. front_door"
                value={filters.dzType}
                onChange={(e) => updateUrl({ dzType: e.target.value })}
                data-testid="filter-dz-type"
              />
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Date from</div>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => updateUrl({ dateFrom: e.target.value })}
                data-testid="filter-date-from"
              />
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Date to</div>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => updateUrl({ dateTo: e.target.value })}
                data-testid="filter-date-to"
              />
            </div>
          </div>

          {/* SPEC-annotation-review-concurrency Phase 6 — Show in-progress toggle */}
          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              id="show-in-progress-toggle"
              checked={filters.showInProgress}
              onChange={(e) => updateUrl({ showInProgress: e.target.checked })}
              data-testid="toggle-show-in-progress"
            />
            <label
              htmlFor="show-in-progress-toggle"
              className="text-sm cursor-pointer"
            >
              Show in-progress (rows currently claimed by another reviewer, greyed out)
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Queue</CardTitle>
            <CardDescription data-testid="text-pagination-summary">
              {isLoading
                ? "Loading…"
                : `Showing ${pageStart}–${pageEnd} of ${total}`}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={filters.offset === 0 || isLoading}
              onClick={() => updateUrl({ offset: Math.max(0, filters.offset - PAGE_SIZE) })}
              data-testid="button-prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={filters.offset + rows.length >= total || isLoading}
              onClick={() => updateUrl({ offset: filters.offset + PAGE_SIZE })}
              data-testid="button-next-page"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div
              className="flex items-center gap-2 text-sm text-destructive py-4"
              data-testid="error-banner"
            >
              <AlertCircle className="h-4 w-4" />
              <span>{(error as Error).message}</span>
            </div>
          )}
          {isLoading ? (
            <div className="space-y-2" data-testid="loading-skeleton">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <div
              className="text-sm text-muted-foreground py-8 text-center"
              data-testid="empty-state"
            >
              No deliveries match the current filters.
            </div>
          ) : (
            <Table data-testid="queue-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Delivery</TableHead>
                  <TableHead>Photo</TableHead>
                  <TableHead>dz_type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Region counts</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  // SPEC-annotation-review-concurrency Phase 6 — claimed-by-
                  // other rows render greyed out and are not clickable. The
                  // visibility filter on the server only returns these rows
                  // when include_claimed=1 is set, so we can detect them
                  // purely by `row.claimerId` being present and not equal to
                  // the current user.
                  const claimedByOther =
                    !!row.claimerId && row.claimerId !== user?.email;
                  return (
                  <TableRow
                    key={row.id}
                    data-testid={`queue-row-${row.id}`}
                    style={claimedByOther ? { opacity: 0.55 } : undefined}
                  >
                    <TableCell className="font-mono text-xs">
                      {claimedByOther ? (
                        <span
                          className="text-muted-foreground"
                          data-testid={`row-locked-${row.id}`}
                        >
                          {row.id}
                        </span>
                      ) : (
                        <Link
                          href={row.detailHref}
                          className="text-primary hover:underline"
                          data-testid={`row-link-${row.id}`}
                        >
                          {row.id}
                        </Link>
                      )}
                      {claimedByOther && row.claimedAt && (
                        <Badge
                          variant="outline"
                          className="ml-2"
                          data-testid={`claim-chip-${row.id}`}
                        >
                          {row.claimerName ?? row.claimerId} —{" "}
                          {formatClaimTimeLeft(row.claimedAt, REVIEW_CLAIM_TTL_MS)}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.thumbnailUrl ? (
                        <img
                          src={row.thumbnailUrl}
                          alt={`Delivery ${row.id} thumbnail`}
                          className="h-10 w-10 rounded object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded bg-muted" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {row.dzTypes.length === 0 ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          row.dzTypes.map((dz) => (
                            <Badge
                              key={dz}
                              variant="outline"
                              data-testid={`dz-chip-${row.id}-${dz}`}
                            >
                              {dz}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={row.regionReviewStatus} />
                    </TableCell>
                    <TableCell>
                      <div
                        className="flex flex-wrap gap-1 text-xs"
                        data-testid={`counts-${row.id}`}
                      >
                        {Object.keys(row.regionCountsByClass).length === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          Object.entries(row.regionCountsByClass)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([cls, count]) => (
                              <Badge
                                key={cls}
                                variant="secondary"
                                data-testid={`count-${row.id}-${cls}`}
                              >
                                {cls}: {count}
                              </Badge>
                            ))
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
